import { toUserMessage } from './errors.js';
// Aggiornamenti OTA dell'app via Supabase Storage.
// Il bundle (zip della cartella dist/) viene scaricato e applicato senza
// passare dal Play Store. Funziona solo in produzione (APK senza server.url).

import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { dbg } from './debug/debugLog.js';
import { closeLocalDb, flushLocalDb, getDeviceId } from './db/local.js';
import { getSyncEngine } from './sync/engine.js';
import { getSupabase } from './supabase.js';
import { compareBundleVersions, isUpdateAllowed } from './updateVersion.js';

// Manifest letto via edge function (NON dallo Storage diretto). L'edge funzione
// `update-manifest` riemette il body con `Cache-Control: no-store` (più rigido
// del `no-cache` che Cloudflare assegna allo Storage), così proxy/VPN/CDN
// intermedi non possono memorizzare la risposta. Soluzione al bug 2026-05-07
// per cui il telefono dell'utente (con VPN attiva) continuava a vedere il
// manifest vecchio per minuti dopo la publish.
const MANIFEST_URL =
  (import.meta.env.VITE_SUPABASE_URL as string) +
  '/functions/v1/update-manifest';

export interface UpdateManifest {
  version: string;
  url: string;
  notes: string;
  published_at: string;
  /** Emergency-only: lets an explicitly authorized manifest replace a newer bundle. */
  allow_downgrade?: boolean;
  /** True when the owner pinned this exact version to the current device. */
  managed_target?: boolean;
  /** True only for shared-tablet accounts or an explicit per-device target. */
  automatic?: boolean;
}

export interface UpdateCheckResult {
  available: boolean;
  current: string;
  latest: string | null;
  notes: string | null;
  /**
   * Quando la verifica fallisce per ragioni infrastrutturali (manifest URL
   * irraggiungibile, errore HTTP, JSON malformato), `available` è false ma
   * `error` riempito così l'UI può distinguere "non c'è update" da "non
   * sono riuscito a controllare". Senza questo, l'utente vede "App già
   * aggiornata" anche quando il manifest era irraggiungibile.
   */
  error: string | null;
  manifestUrl: string;
  automatic: boolean;
}

function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

function isDevServerActive(): boolean {
  // Il check NON deve usare solo `import.meta.env.DEV`: se la build viene fatta
  // con NODE_ENV=development per errore, anche un APK reale verrebbe trattato
  // come dev e l'OTA si bloccherebbe (`available` sempre false). Su native
  // l'OTA è sempre legittimo, anche se DEV è true (build sbagliata).
  return !isNative() && import.meta.env.DEV;
}

/**
 * Conferma al plugin LiveUpdate che l'app è partita correttamente. Va chiamata
 * UNA VOLTA al boot, il prima possibile (idealmente prima del primo render
 * pesante). Se non viene chiamata entro `readyTimeout` (default 10s) il plugin
 * presume che il bundle nuovo sia rotto e fa rollback al bundle di default
 * (quello compilato nell'APK). Sintomo del rollback: la home aggiornata
 * appare per qualche secondo poi torna quella vecchia.
 */
let readyCalled = false;
export async function notifyAppReady(): Promise<void> {
  if (!isNative() || readyCalled) return;
  // T-321: conferma al plugin LiveUpdate che il bundle è partito, con RETRY.
  // Due bug storici causavano il rollback al bundle dell'APK (sintomo: "Avvio…"
  // per qualche secondo poi 0.0.0, verificato su Xiaomi 14):
  //   1) il guard `readyCalled` veniva settato PRIMA dell'await → se la chiamata
  //      falliva (bridge Capacitor non ancora pronto) restava marcata come fatta
  //      e non si ritentava → mai confermato;
  //   2) un singolo tentativo poteva cadere in una finestra in cui il bridge non
  //      era pronto.
  // Soluzione: ritenta finché ready() non va a buon fine, settando il guard SOLO
  // dopo il successo. ~16 tentativi × 350ms = entro il readyTimeout del plugin.
  for (let attempt = 0; attempt < 16 && !readyCalled; attempt += 1) {
    try {
      await LiveUpdate.ready();
      readyCalled = true;
      return;
    } catch {
      // Bridge non pronto / plugin assente (es. web di test): ritenta a breve.
      await new Promise((r) => setTimeout(r, 350));
    }
  }
}

interface ManifestFetch {
  manifest: UpdateManifest | null;
  error: string | null;
}

/** Recupera il manifest dal bucket Supabase (cache-busted). Ritorna anche
 *  l'errore così l'UI può distinguere "no-update" da "fetch fallita". */
/**
 * Token di sessione Supabase, se l'utente è loggato. Inviato come Bearer alla
 * edge function `update-manifest` così può decidere il canale (beta vs stable)
 * in base all'allowlist `BETA_TESTERS`. Best-effort: se non c'è sessione o la
 * lettura fallisce si procede senza token → l'utente riceve lo stabile (il
 * default sicuro). update-manifest ha verify_jwt=false: il token è opzionale.
 */
async function sessionBearer(): Promise<string | null> {
  try {
    const { data } = await getSupabase().auth.getSession();
    return data.session?.access_token ?? null;
  } catch {
    return null;
  }
}

async function fetchManifest(currentVersion: string): Promise<ManifestFetch> {
  if (isNative() && navigator.onLine === false) {
    return { manifest: null, error: 'offline: verifica aggiornamenti non disponibile' };
  }
  const urlObject = new URL(MANIFEST_URL);
  urlObject.searchParams.set('t', String(Date.now()));
  const t0 = performance.now();
  const controller = new AbortController();
  let timeout = 0;
  // Identity/session reads can wait on SQLite/auth locks and cannot be aborted.
  // Bound the whole check and prevent their late completion from starting HTTP.
  const deadline = new Promise<never>((_resolve, reject) => {
    timeout = window.setTimeout(() => {
      controller.abort(new DOMException('Verifica aggiornamenti scaduta.', 'TimeoutError'));
      reject(controller.signal.reason);
    }, 8_000);
  });
  const request = async (): Promise<ManifestFetch> => {
    if (isNative()) {
      try {
        urlObject.searchParams.set('device_id', await getDeviceId());
        urlObject.searchParams.set('current_version', currentVersion);
      } catch (err) {
        dbg.warn('updater', 'device_identity_failed', { error: toUserMessage(err) });
      }
    }
    if (controller.signal.aborted) throw controller.signal.reason;
    const url = urlObject.toString();
    const token = await sessionBearer();
    if (controller.signal.aborted) throw controller.signal.reason;
    const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await fetch(url, { cache: 'no-store', headers, signal: controller.signal });
    if (controller.signal.aborted) throw controller.signal.reason;
    const ms = Math.round(performance.now() - t0);
    if (!res.ok) {
      dbg.error('updater', 'manifest_http_error', { url, status: res.status, ms });
      return { manifest: null, error: `HTTP ${res.status}` };
    }
    const text = await res.text();
    if (controller.signal.aborted) throw controller.signal.reason;
    dbg.info('updater', 'manifest_fetched', { url, status: res.status, ms, body_len: text.length, body_preview: text.slice(0, 200) });
    let json: UpdateManifest;
    try {
      json = JSON.parse(text) as UpdateManifest;
    } catch (parseErr) {
      dbg.error('updater', 'manifest_parse_failed', {
        url, body_preview: text.slice(0, 200), error: (parseErr as Error).message,
      });
      return { manifest: null, error: `parse: ${(parseErr as Error).message}` };
    }
    if (!json?.version) {
      dbg.error('updater', 'manifest_missing_version', { json });
      return { manifest: null, error: 'manifest senza version' };
    }
    return { manifest: json, error: null };
  };
  try {
    return await Promise.race([request(), deadline]);
  } catch (err) {
    const e = err as Error;
    dbg.error('updater', 'manifest_fetch_failed', {
      url: urlObject.toString(), ms: Math.round(performance.now() - t0),
      error_type: e.name, error_message: e.message, online: navigator.onLine,
    });
    return { manifest: null, error: `${e.name}: ${e.message}` };
  } finally {
    window.clearTimeout(timeout);
  }
}

/** Versione del bundle attualmente in esecuzione. */
export async function getCurrentVersion(): Promise<string> {
  const envVer = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? '0.0.0';
  if (!isNative()) return envVer;
  try {
    const result = await LiveUpdate.getBundle();
    const bundleId = result?.bundleId ?? null;
    dbg.info('updater', 'current_version', { bundleId, envVer, raw_result: result });
    return bundleId ?? envVer;
  } catch (err) {
    dbg.warn('updater', 'getBundle_failed', { error: toUserMessage(err), fallback: envVer });
    return envVer;
  }
}

/** Controlla se esiste un aggiornamento senza scaricarlo. */
export async function checkForUpdate(): Promise<UpdateCheckResult> {
  const current = await getCurrentVersion();
  const base: Omit<UpdateCheckResult, 'available' | 'latest' | 'notes' | 'error'> = {
    current,
    manifestUrl: MANIFEST_URL,
    automatic: false,
  };
  if (!isNative() || isDevServerActive()) {
    return { ...base, available: false, latest: null, notes: null, error: null };
  }
  const { manifest, error } = await fetchManifest(current);
  if (!manifest) {
    return { ...base, available: false, latest: null, notes: null, error };
  }
  const comparison = compareBundleVersions(current, manifest.version);
  const allowDowngrade = manifest.allow_downgrade === true;
  const available = isUpdateAllowed(current, manifest.version, allowDowngrade);
  dbg.info('updater', 'check_result', {
    current,
    latest: manifest.version,
    comparison,
    allow_downgrade: allowDowngrade,
    available,
    automatic: manifest.automatic === true,
    manifest_url: MANIFEST_URL,
  });
  return {
    ...base,
    available,
    latest: manifest.version,
    notes: manifest.notes,
    error: null,
    automatic: manifest.automatic === true,
  };
}

/**
 * Scarica il bundle e lo schedula come "next" senza riavviare. Il caller
 * deve chiamare `restartAppForUpdate()` quando l'utente conferma il riavvio.
 *
 * NOTA: dal pannello Impostazioni NON usiamo più `LiveUpdate.reload()`, perché
 * a quel punto SQLite e sync sono attivi. Il solo bootstrap T-635 può fare il
 * reload immediato: lì l'albero App non è ancora montato.
 */
export async function downloadAndApplyUpdate(
  onProgress?: (pct: number) => void,
): Promise<void> {
  if (!isNative() || isDevServerActive()) return;

  const current = await getCurrentVersion();
  const { manifest, error } = await fetchManifest(current);
  if (!manifest) throw new Error(`Manifest aggiornamento non raggiungibile: ${error ?? 'errore sconosciuto'} (URL: ${MANIFEST_URL})`);
  if (!isUpdateAllowed(current, manifest.version, manifest.allow_downgrade === true)) {
    throw new Error('La versione proposta non è più recente. Il downgrade richiede un manifest esplicitamente autorizzato.');
  }

  onProgress?.(5);

  // Se il bundle è già stato scaricato in passato `downloadBundle` fallisce
  // con "bundle already exists". OK, andiamo avanti col setNext.
  try {
    await LiveUpdate.downloadBundle({
      bundleId: manifest.version,
      url: manifest.url,
    });
  } catch (err) {
    const msg = String((err as Error)?.message ?? err).toLowerCase();
    const alreadyExists = msg.includes('already exist') || msg.includes('already exists');
    if (!alreadyExists) throw err;
  }

  onProgress?.(85);

  await LiveUpdate.setNextBundle({ bundleId: manifest.version });

  onProgress?.(100);
  dbg.info('updater', 'bundle_ready_for_restart', { version: manifest.version });
}

/**
 * Attiva subito il bundle preparato durante il bootstrap, quando l'albero App
 * non è ancora montato e quindi non esistono sync engine o connessioni SQLite
 * applicative da lasciare in uno stato incoerente.
 */
export async function reloadStagedStartupUpdate(): Promise<void> {
  if (!isNative() || isDevServerActive()) return;
  await LiveUpdate.reload();
}

/**
 * Chiude l’attività Android perché la prossima apertura carichi il bundle
 * appena scaricato. exitApp chiude l’attività, non garantisce la terminazione
 * del processo: i writer devono finire prima della chiusura SQLite.
 *
 * Su Android l'utente deve riaprire l'app a mano (Android non permette ad un
 * app di rilanciarsi).
 */
export async function restartAppForUpdate(): Promise<void> {
  // Attendi i writer prima di chiudere le risorse, così
  // l'eventuale buffer di sync queue viene flushato su disco.
  try {
    await getSyncEngine().pauseForUpdate();
  } catch (err) {
    void getSyncEngine().resumeAfterUpdateFailure();
    throw err;
  }
  try {
    await flushLocalDb();
    await closeLocalDb();
  } catch (err) {
    dbg.warn('updater', 'restart_db_close_failed', { error: toUserMessage(err) });
    void getSyncEngine().resumeAfterUpdateFailure();
    throw err;
  }
  dbg.info('updater', 'restart_exit_app', {});
  // Piccolo delay per garantire che il log sia flushato.
  await new Promise((r) => setTimeout(r, 200));
  try {
    if (Capacitor.getPlatform() === 'ios') await LiveUpdate.reload();
    else await CapApp.exitApp();
  } catch (err) {
    // Se la chiusura dell’attività fallisce, tenta il reload del bundle.
    // In caso di errore riprendi il motore invece di lasciarlo fermo.
    dbg.warn('updater', 'exit_app_failed_fallback_reload', { error: toUserMessage(err) });
    try { await LiveUpdate.reload(); } catch (reloadError) {
      void getSyncEngine().resumeAfterUpdateFailure();
      throw reloadError;
    }
  }
}
